import {Mapper} from '@s-ui/domain'

const PRICE_MIN_REG_EXP = /min/
export class FromSearchJSONToFiltersTrackingMapper extends Mapper {
  constructor({config}) {
    super()

    this._config = config
    this._sortOptions = this._config.get('tracking').SORT
    this._filterNames = this._config.get('FILTER_NAMES')
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._trackingFilterNames = config.get('TRACKING_FILTER_NAMES')
    this._electricChargingTimeTrackingTypes = config.get('COMMON').ELECTRIC_CHARGING_TIME_TRACKING_TYPES
  }

  map = data => {
    const {search, lastFilter: lastFilterData, listingSectionId} = data
    const trackingFilters = this._getFilters({search, listingSectionId})
    const isLastFilterAString = typeof lastFilterData === 'string'
    // String for React and Object for monolito. Clean when migration ends.
    const lastFilter = isLastFilterAString
      ? this._getLastFilter({
          lastFilterKey: lastFilterData,
          trackingFilters,
          search
        })
      : lastFilterData

    return {
      filters: trackingFilters,
      lastFilter
    }
  }

  _getLastFilter({lastFilterKey, trackingFilters, search}) {
    const priceFilterName = this._filterNames.PRICE
    const isPriceFilter = Object.values(this._trackingFilterNames[priceFilterName]).some(key => key === lastFilterKey)

    // Logic for price and financedPrice combo
    // WIP: add instalment tracking
    if (isPriceFilter) return this._getPriceLastFilter({lastFilterKey, trackingFilters, search})

    const isVehiclesFilter = lastFilterKey === this._filterNames.VEHICLES
    if (isVehiclesFilter) return this._getVehiclesLastFilter({filters: trackingFilters, search})

    const isExcludedVehiclesFilter = lastFilterKey === this._filterNames.EXCLUDED_VEHICLES
    if (isExcludedVehiclesFilter) return this._getExcludedVehiclesLastFilter({filters: trackingFilters, search})

    // Default return for rest of filters
    const value = trackingFilters[lastFilterKey]
    const currentValue = [...value]
    const lastItem = Array.isArray(value) ? currentValue.pop() : value

    return {[lastFilterKey]: lastItem}
  }

  _getVehiclesLastFilter = ({filters, search}) => {
    const makeFilters = filters[this._trackingFilterNames[this._filterNames.MAKE]] ?? []
    const modelFilters = filters[this._trackingFilterNames[this._filterNames.MODEL]] ?? []

    const currentMakeValues = [...makeFilters]
    const currentModelValues = [...modelFilters]

    const lastMakePosition = currentMakeValues.length - 1
    const lastMakeValue = currentMakeValues.pop()
    const lastModelValue = currentModelValues.at(lastMakePosition)

    const {vehicles = []} = search
    const lastVehicle = vehicles[lastMakePosition]

    return {
      [this._trackingFilterNames[this._filterNames.MAKE]]: lastMakeValue,
      brand_id: lastVehicle?.makeId,
      [this._trackingFilterNames[this._filterNames.MODEL]]: lastModelValue,
      model_id: lastVehicle?.model?.id
    }
  }

  _getExcludedVehiclesLastFilter = ({filters, search}) => {
    const {MAKE: excludeMakeKey, MODEL: excludeModelKey} =
      this._trackingFilterNames[this._filterNames.EXCLUDED_VEHICLES]

    const makeFilters = filters[excludeMakeKey] ?? []
    const modelFilters = filters[excludeModelKey] ?? []

    const currentMakeValues = [...makeFilters]
    const currentModelValues = [...modelFilters]

    const lastMakePosition = currentMakeValues.length - 1
    const lastMakeValue = currentMakeValues.pop()
    const lastModelValue = currentModelValues.at(lastMakePosition)

    const {excludedVehicles = []} = search
    const lastVehicle = excludedVehicles[lastMakePosition]

    return {
      brand_excluded: lastMakeValue,
      brand_excluded_id: lastVehicle?.makeId,
      model_excluded: lastModelValue,
      model_excluded_id: lastVehicle?.model?.id
    }
  }

  _getPriceLastFilter({lastFilterKey, trackingFilters, search}) {
    const {financedPrice} = search
    const isFinancedPrice = financedPrice === true
    if (isFinancedPrice) {
      const financedPriceTrackingFilterNames = this._trackingFilterNames[this._filterNames.FINANCED_PRICE]

      const financedPriceKey = lastFilterKey.match(PRICE_MIN_REG_EXP)
        ? financedPriceTrackingFilterNames.FROM
        : financedPriceTrackingFilterNames.TO

      return {[financedPriceKey]: trackingFilters[financedPriceKey]}
    }

    const value = trackingFilters[lastFilterKey]
    return {[lastFilterKey]: value}
  }

  _getFilters({listingSectionId, search}) {
    const hasSearch = Boolean(search)
    if (!hasSearch) return

    const {
      batteryCapacity,
      bodyTypeIds,
      category2Id,
      chargingTimeFastMode,
      chargingTimeStandardMode,
      city,
      colorIds,
      commitmentMonths,
      doors,
      drivenWheelsIds,
      electricAutonomy,
      entry,
      environmentalLabels,
      equipments,
      excludedVehicles,
      filteredOfferTypeId,
      financedPrice,
      fuelTypeIds,
      hasInstalment,
      hasOnlineFinancing,
      hasPhoto,
      hasPriceDrop,
      hasReservation,
      hasStock,
      hasWarranty,
      hp,
      instalment,
      isCertified,
      kms,
      litres,
      location,
      luggageCapacity,
      maxTerms,
      monthlySubscriptionFee,
      offerTypeId,
      onlyPeninsula,
      price,
      priceRank,
      provinceIds,
      rating,
      searchText,
      seats,
      sellerTypeId,
      sortBy,
      sortOrder,
      subscriptionVehicleState,
      targetBuyer,
      transmissionTypeId,
      vehicles,
      year
    } = search
    const priceInterval = hasInstalment ? instalment : price
    return this._removeUndefinedValues({
      ...this._getBodyTypes({bodyTypeIds}),
      ...this._getCategory2({category2Id}),
      ...this._getCertified({isCertified}),
      ...this._getCity({city}),
      ...this._getColor({colorIds}),
      ...this._getCommitmentMonths({commitmentMonths}),
      ...this._getCubicCapacity({litres}),
      ...this._getDoors({doors}),
      ...this._getDrivenWheels({drivenWheelsIds}),
      ...this._getEnvironmentalLabels({environmentalLabels}),
      ...this._getEquipments({equipments}),
      ...this._getFuelTypes({fuelTypeIds}),
      ...this._getHp({hp}),
      ...this._getKeywords({searchText}),
      ...this._getKms({kms}),
      ...this._getLitres({litres}),
      ...this._getLocation({location}),
      ...this._getLuggageCapacity({luggageCapacity}),
      ...this._getMonthlySubscriptionFee({monthlySubscriptionFee}),
      ...this._getOfferType({offerTypeId, filteredOfferTypeId, listingSectionId}),
      ...this._getOnlyPeninsula({onlyPeninsula}),
      ...this._getOrder({sortBy, sortOrder}),
      ...this._getPhoto({hasPhoto}),
      ...this._getStock({hasStock}),
      ...this._getPrice({priceInterval, financedPrice, hasInstalment}),
      ...this._getPriceRank({priceRank}),
      ...this._getProvinces({provinceIds}),
      ...this._getRating({rating}),
      ...this._getSeats({seats}),
      ...this._getSellerType({sellerTypeId}),
      ...this._getTargetBuyer({targetBuyer}),
      ...this._getTransmission({transmissionTypeId}),
      ...this._getSubscriptionVehicleState({subscriptionVehicleState}),
      ...this._getWarranty({hasWarranty}),
      ...this._getYear({year}),
      ...this._getVehicles({vehicles}),
      ...this._getExcludedVehicles({excludedVehicles}),
      ...this._getFinancingEntry({entry}),
      ...this._getFinancingMaxTerms({maxTerms}),
      ...this._getElectricAutonomy({electricAutonomy}),
      ...this._getBatteryCapacity({batteryCapacity}),
      ...this._getChargingTimeFastMode({chargingTimeFastMode}),
      ...this._getChargingTimeStandardMode({chargingTimeStandardMode}),
      ...this._getHasReservation({hasReservation}),
      ...this._getHasOnlineFinancing({hasOnlineFinancing}),
      ...this._getHasPriceDrop({hasPriceDrop})
    })
  }

  _removeUndefinedValues = object => JSON.parse(JSON.stringify(object))

  _getFilterValue({filterName, value}) {
    if (!value) return {}
    const trackingFilterName = this._trackingFilterNames[filterName]

    return {[trackingFilterName]: value}
  }

  // Abstraction for conditionally getting the values of filters with an array as shape
  _getFilterArrayValues({filterValues = [], filterKey, getFilterLabelById = () => {}}) {
    if (!filterValues && filterValues.length) return {}

    const itemsMatched = filterValues.map(getFilterLabelById)
    if (!itemsMatched.length) return {}

    return {[filterKey]: itemsMatched}
  }

  // Abstraction for conditionally getting the values of filter with a {from, to} interval shape
  _getFilterIntervalValues({intervalObject = {}, trackingFilterName, toType = value => String(value)}) {
    const {from, to} = intervalObject

    return {
      ...(from && {[trackingFilterName.FROM]: toType(from)}),
      ...(to && {[trackingFilterName.TO]: toType(to)})
    }
  }

  _getBooleanFilterValue({filterName, value}) {
    if (!value) return {}

    return {[this._trackingFilterNames[filterName]]: value}
  }

  _idValuesToLocalizedTrackingObject({arrayValue, filterName, translationsConfig}) {
    const hasValues = arrayValue && arrayValue.length
    if (!hasValues) return {}

    const trackingFilterName = this._trackingFilterNames[filterName]

    const localizedValues = arrayValue.map(id => translationsConfig[id]?.[this._culture])

    if (!localizedValues.length) return {}

    return {[trackingFilterName]: localizedValues}
  }

  // Individual filter mappers
  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓

  _getVehicles = ({vehicles = []}) => {
    const makeFilterName = this._filterNames.MAKE
    const makeTrackingFilterName = this._trackingFilterNames[makeFilterName]
    const makes = vehicles.map(vehicle => vehicle.make).filter(Boolean)

    const modelFilterName = this._filterNames.MODEL
    const modelTrackingFilterName = this._trackingFilterNames[modelFilterName]
    const models = vehicles.map(vehicle => vehicle.model?.name).filter(Boolean)

    const versionFilterName = this._filterNames.VERSION
    const versionTrackingFilterName = this._trackingFilterNames[versionFilterName]
    const versions = vehicles.map(vehicle => vehicle.version).filter(Boolean)

    const trakingMakes = this._getFilterArrayValues({
      filterValues: makes,
      filterKey: makeTrackingFilterName,
      getFilterLabelById: make => make
    })

    const trakingModels = this._getFilterArrayValues({
      filterValues: models,
      filterKey: modelTrackingFilterName,
      getFilterLabelById: model => model
    })

    const trakingVersions = this._getFilterArrayValues({
      filterValues: versions,
      filterKey: versionTrackingFilterName,
      getFilterLabelById: version => version
    })

    return {...trakingMakes, ...trakingModels, ...trakingVersions}
  }

  _getExcludedVehicles = ({excludedVehicles = []}) => {
    const {MAKE: excludeMakeKey, MODEL: excludeModelKey} =
      this._trackingFilterNames[this._filterNames.EXCLUDED_VEHICLES]

    const makes = excludedVehicles.map(vehicle => vehicle.make).filter(Boolean)
    const models = excludedVehicles.map(vehicle => vehicle.model?.name).filter(Boolean)

    return {
      ...this._getFilterArrayValues({
        filterValues: makes,
        filterKey: excludeMakeKey,
        getFilterLabelById: make => make
      }),
      ...this._getFilterArrayValues({
        filterValues: models,
        filterKey: excludeModelKey,
        getFilterLabelById: model => model
      })
    }
  }

  _getOrder({sortBy = '', sortOrder}) {
    const option = `${sortBy}_${sortOrder}`
    const filterName = this._filterNames.SORT_BY
    const trackingFilterName = this._trackingFilterNames[filterName]

    return {[trackingFilterName]: this._sortOptions[option]}
  }

  _getOfferType({offerTypeId: paramOfferTypeId, filteredOfferTypeId, listingSectionId}) {
    const filterName = this._filterNames.OFFER_TYPE

    const trackingFilterName = this._trackingFilterNames[filterName]
    const {
      TRACKING_FILTERS_OFFER_TYPES_TRANSLATIONS: offerTypesTranslations,
      OFFER_TYPES_TRANSLATIONS: offerTypesFilterTranslations
    } = this._config.get('tracking')

    const {OFFER_TYPE_NEW} = this._config.get('search')
    const {LISTING_SECTION_IDS} = this._config.get('COMMON')

    const offerTypeId =
      filteredOfferTypeId === OFFER_TYPE_NEW || String(listingSectionId) === LISTING_SECTION_IDS.ALL_VEHICLES
        ? filteredOfferTypeId
        : paramOfferTypeId

    const offerTypeValue = offerTypesTranslations[offerTypeId]
      ? offerTypesTranslations[offerTypeId][this._culture]
      : undefined

    const offerTypeFilterValue = offerTypesFilterTranslations[filteredOfferTypeId]
      ? offerTypesFilterTranslations[filteredOfferTypeId][this._culture]
      : undefined

    return {
      [this._trackingFilterNames.offerType]: offerTypeValue,
      ...(offerTypeFilterValue && {[trackingFilterName]: offerTypeFilterValue})
    }
  }

  _getCategory2({category2Id}) {
    const filterName = this._filterNames.CATEGORY_2
    const {CATEGORIES_LEVEL_2_TRANSLATIONS: category2Translations} = this._config.get('tracking')

    const filterValue = category2Translations[category2Id]
      ? category2Translations[category2Id][this._culture]
      : undefined

    return this._getFilterValue({filterName, value: filterValue})
  }

  _getKeywords({searchText}) {
    return this._getFilterValue({
      filterName: this._filterNames.SEARCH_TEXT,
      value: searchText
    })
  }

  _getMake({make}) {
    return this._getFilterValue({
      filterName: this._filterNames.MAKE,
      value: make
    })
  }

  _getModel({model: {name: modelName} = {}}) {
    return this._getFilterValue({
      filterName: this._filterNames.MODEL,
      value: modelName
    })
  }

  // financedPrice doesn't have a default value = false because for stock pages this can be null and we don't have to send it
  _getPrice({priceInterval = {}, financedPrice, hasInstalment}) {
    const financedPriceFilterName = this._filterNames.IS_FINANCED
    const {TRACKING_FILTERS_PAYMENT_TYPES_TRANSLATIONS} = this._config.get('tracking')
    const trackingFilterName = hasInstalment ? 'INSTALMENT' : 'PRICE'
    const trackingFilterKy = this._filterNames[trackingFilterName]

    const priceValue = hasInstalment ? 'INSTALMENT' : 'CASH'

    return {
      ...this._getFilterIntervalValues({
        intervalObject: priceInterval,
        trackingFilterName: this._trackingFilterNames[trackingFilterKy]
      }),
      ...(hasInstalment && {
        [this._trackingFilterNames[financedPriceFilterName]]: TRACKING_FILTERS_PAYMENT_TYPES_TRANSLATIONS[priceValue]
      })
    }
  }

  _getMonthlySubscriptionFee({monthlySubscriptionFee = {}}) {
    const trackingFilterName = this._trackingFilterNames[this._filterNames.MONTHLY_SUBSCRIPTION_FEE]

    return this._getFilterIntervalValues({
      intervalObject: monthlySubscriptionFee,
      trackingFilterName,
      toType: value => Number(value)
    })
  }

  _getYear({year = {}}) {
    const trackingFilterName = this._trackingFilterNames[this._filterNames.YEAR]

    return this._getFilterIntervalValues({
      intervalObject: year,
      trackingFilterName,
      toType: value => Number(value)
    })
  }

  _getKms({kms = {}}) {
    const trackingFilterName = this._trackingFilterNames[this._filterNames.KMS]

    return this._getFilterIntervalValues({
      intervalObject: kms,
      trackingFilterName
    })
  }

  _getProvinces({provinceIds = []}) {
    const filterName = this._filterNames.PROVINCE
    const trackingFilterName = this._trackingFilterNames[filterName]
    const provinces = this._config.get('provinces')
    const getProvinceLabelById = id => {
      const validProvinceIds = provinces.find(({id: provinceId}) => provinceId === id)
      return validProvinceIds?.label
    }

    const provinceLabels = this._getFilterArrayValues({
      filterValues: provinceIds,
      filterKey: trackingFilterName,
      getFilterLabelById: getProvinceLabelById
    })

    return provinceLabels[trackingFilterName]?.length && provinceLabels[trackingFilterName][0] !== undefined
      ? provinceLabels
      : {}
  }

  _getBodyTypes({bodyTypeIds}) {
    const filterName = this._filterNames.BODY_TYPE
    const {BODY_TYPES_TRANSLATIONS: bodyTypesTranslations} = this._config.get('tracking')

    return this._idValuesToLocalizedTrackingObject({
      arrayValue: bodyTypeIds,
      filterName,
      translationsConfig: bodyTypesTranslations
    })
  }

  _getCommitmentMonths({commitmentMonths = []}) {
    const filterName = this._filterNames.MONTHLY_SUBSCRIPTION_COMMITMENT
    const trackingFilterName = this._trackingFilterNames[filterName]

    if (!commitmentMonths || !commitmentMonths.length) return {}

    return {[trackingFilterName]: commitmentMonths}
  }

  _getFuelTypes({fuelTypeIds = []}) {
    const filterName = this._filterNames.FUEL_TYPE
    const {TRACKING_FUEL_TYPES_FILTERS_TRANSLATIONS: fuelTypesTranslations} = this._config.get('tracking')

    return this._idValuesToLocalizedTrackingObject({
      arrayValue: fuelTypeIds,
      filterName,
      translationsConfig: fuelTypesTranslations
    })
  }

  _getColor({colorIds}) {
    const filterName = this._filterNames.COLOR
    const {COLOR_TRANSLATIONS: colorTranslations} = this._config.get('tracking')

    return this._idValuesToLocalizedTrackingObject({
      arrayValue: colorIds,
      filterName,
      translationsConfig: colorTranslations
    })
  }

  _getDoors({doors}) {
    const filterName = this._filterNames.DOOR
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: doors,
      trackingFilterName
    })
  }

  _getDrivenWheels({drivenWheelsIds = []}) {
    const filterName = this._filterNames.DRIVEN_WHEELS
    const {TRACKING_DRIVEN_WHEELS_FILTERS_TRANSLATIONS: drivenWheelsTranslations} = this._config.get('tracking')

    return this._idValuesToLocalizedTrackingObject({
      arrayValue: drivenWheelsIds,
      filterName,
      translationsConfig: drivenWheelsTranslations
    })
  }

  _getEnvironmentalLabels({environmentalLabels = []}) {
    const filterName = this._filterNames.ENVIRONMENTAL_LABEL
    const {TRACKING_ENVIRONMENTAL_LABELS_FILTERS_TRANSLATIONS: environmentalLabelsTranslations} =
      this._config.get('tracking')

    return this._idValuesToLocalizedTrackingObject({
      arrayValue: environmentalLabels,
      filterName,
      translationsConfig: environmentalLabelsTranslations
    })
  }

  _getSubscriptionVehicleState({subscriptionVehicleState}) {
    return this._getFilterValue({
      filterName: this._filterNames.VEHICLE_STATE,
      value: subscriptionVehicleState
    })
  }

  _getVersion({version}) {
    return this._getFilterValue({
      filterName: this._filterNames.VERSION,
      value: version
    })
  }

  _getTargetBuyer({targetBuyer}) {
    return this._getFilterValue({
      filterName: this._filterNames.TARGET_BUYER,
      value: targetBuyer
    })
  }

  _getTransmission({transmissionTypeId}) {
    if (!transmissionTypeId) return {}

    const filterName = this._filterNames.TRANSMISSION_TYPE
    const trackingFilterName = this._trackingFilterNames[filterName]
    const {TRANSMISSION_TRANSLATIONS} = this._config.get('tracking')

    return {
      [trackingFilterName]: TRANSMISSION_TRANSLATIONS[transmissionTypeId][this._culture]
    }
  }

  _getHp({hp}) {
    const filterName = this._filterNames.HP
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: hp,
      trackingFilterName
    })
  }

  _getLitres({litres}) {
    const filterName = this._filterNames.LITRES
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: litres,
      trackingFilterName
    })
  }

  _getSeats({seats}) {
    const filterName = this._filterNames.SEAT
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: seats,
      trackingFilterName
    })
  }

  _getEquipments({equipments}) {
    const filterName = this._filterNames.EQUIPMENTS

    return this._getFilterArrayValues({
      filterValues: equipments,
      filterKey: this._trackingFilterNames[filterName],
      getFilterLabelById: equipment => equipment
    })
  }

  _getSellerType({sellerTypeId}) {
    if (!sellerTypeId) return {}

    const filterName = this._filterNames.SELLER_TYPE
    const trackingFilterName = this._trackingFilterNames[filterName]
    const {SELLER_TYPES_TRANSLATIONS: translationsSellerTypes} = this._config.get('tracking')

    return {
      [trackingFilterName]: translationsSellerTypes[sellerTypeId][this._culture]
    }
  }

  _getLuggageCapacity({luggageCapacity = {}}) {
    const {luggageCapacityTypes = []} = this._config.get('search')
    const selectedLuggageCapacity = luggageCapacityTypes.find(
      type => JSON.stringify(type.value) === JSON.stringify(luggageCapacity)
    )

    if (!selectedLuggageCapacity) return {}

    const {LUGGAGE_CAPACITY_TRANSLATIONS: luggageCapacityTranslations} = this._config.get('tracking')
    const trackingFilterName = this._trackingFilterNames[this._filterNames.LUGGAGE_CAPACITY]
    const value = luggageCapacityTranslations[selectedLuggageCapacity.id][this._culture]

    return {[trackingFilterName]: value}
  }

  _getCubicCapacity({litres = {}}) {
    const filterName = this._filterNames.LITRES
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: litres,
      trackingFilterName
    })
  }

  _getWarranty({hasWarranty}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.HAS_WARRANTY,
      value: hasWarranty
    })
  }

  _getCertified({isCertified}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.IS_CERTIFIED,
      value: isCertified
    })
  }

  _getPhoto({hasPhoto}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.HAS_PHOTO,
      value: hasPhoto
    })
  }

  _getStock({hasStock}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.HAS_STOCK,
      value: hasStock
    })
  }

  _getOnlyPeninsula({onlyPeninsula}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.ONLY_PENINSULA,
      value: onlyPeninsula
    })
  }

  _getFinancingEntry({entry}) {
    return this._getFilterValue({filterName: this._filterNames.FINANCING_ENTRY, value: entry})
  }

  _getFinancingMaxTerms({maxTerms}) {
    return this._getFilterValue({
      filterName: this._filterNames.FINANCING_TERMS,
      value: maxTerms
    })
  }

  _getRating({rating = {}}) {
    const filterName = this._filterNames.RATING
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: rating,
      trackingFilterName
    })
  }

  _getPriceRank({priceRank}) {
    const {PRICE_RANK_TRANSLATIONS} = this._config.get('tracking')
    return this._getFilterArrayValues({
      filterValues: priceRank,
      filterKey: this._trackingFilterNames[this._filterNames.PRICE_RANK],
      getFilterLabelById: priceRankLabel => PRICE_RANK_TRANSLATIONS[priceRankLabel]
    })
  }

  _getElectricAutonomy({electricAutonomy}) {
    const filterName = this._filterNames.ELECTRIC_AUTONOMY
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: electricAutonomy,
      trackingFilterName
    })
  }

  _getBatteryCapacity({batteryCapacity}) {
    const filterName = this._filterNames.ELECTRIC_BATTERY_CAPACITY
    const trackingFilterName = this._trackingFilterNames[filterName]

    return this._getFilterIntervalValues({
      intervalObject: batteryCapacity,
      trackingFilterName
    })
  }

  _getChargingTimeFastMode({chargingTimeFastMode}) {
    const chargingTimeFastModeFilterName = this._filterNames.ELECTRIC_CHARGING_TIME_FAST_MODE
    const trackingFilterName = this._trackingFilterNames[chargingTimeFastModeFilterName]

    const hasValue = chargingTimeFastMode?.from || chargingTimeFastMode?.to

    return {
      ...(hasValue && {
        ...this._getFilterIntervalValues({intervalObject: chargingTimeFastMode, trackingFilterName}),
        [trackingFilterName.TYPE]: this._electricChargingTimeTrackingTypes[chargingTimeFastModeFilterName]
      })
    }
  }

  _getChargingTimeStandardMode({chargingTimeStandardMode}) {
    const chargingTimeStandardModeFilterName = this._filterNames.ELECTRIC_CHARGING_TIME_STANDARD_MODE
    const trackingFilterName = this._trackingFilterNames[chargingTimeStandardModeFilterName]

    const hasValue = chargingTimeStandardMode?.from || chargingTimeStandardMode?.to

    return {
      ...(hasValue && {
        ...this._getFilterIntervalValues({intervalObject: chargingTimeStandardMode, trackingFilterName}),
        [trackingFilterName.TYPE]: this._electricChargingTimeTrackingTypes[chargingTimeStandardModeFilterName]
      })
    }
  }

  _getLocation({location}) {
    const {radio, text} = location || {}

    return {
      ...(radio && {[this._trackingFilterNames[this._filterNames.LOCATION_RADIO]]: String(radio)}),
      ...(text && {[this._trackingFilterNames[this._filterNames.LOCATION_TEXT]]: String(text)})
    }
  }

  _getHasReservation({hasReservation}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.HAS_RESERVATION,
      value: hasReservation
    })
  }

  _getHasOnlineFinancing({hasOnlineFinancing}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.HAS_ONLINE_FINANCING,
      value: hasOnlineFinancing
    })
  }

  _getHasPriceDrop({hasPriceDrop}) {
    return this._getBooleanFilterValue({
      filterName: this._filterNames.HAS_PRICE_DROP,
      value: hasPriceDrop
    })
  }

  _getCity({city}) {
    return this._getFilterValue({
      filterName: this._filterNames.CITY,
      value: city
    })
  }
}
